import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { notify } from '../../helper/notify';
import '../../scss/request-credit-invoice-modal.style.scss';
import apiCall from '../../../api';

interface RequestCreditInvoiceModalProps {
    show: boolean;
    invoice: any;
    onClose: () => void;
    onSuccess?: () => void;
    onEditInvoice?: (invoice: any) => void;
}

/**
 * Modal for requesting a credit invoice with reference and description
 * Designed as a confirmation modal with embedded form
 */
const RequestCreditInvoiceModal: React.FC<RequestCreditInvoiceModalProps> = ({
    show,
    invoice,
    onClose,
    onSuccess,
    onEditInvoice
}) => {
    const { t } = useTranslation();
    const [reference, setReference] = useState('');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const [referenceError, setReferenceError] = useState(false);
    const [isRequestCreated, setIsRequestCreated] = useState(false);
    const [isCreditInvoiceAccept, setIsCreditInvoiceAccept] = useState(false);
    const [isRequestApproved, setIsRequestApproved] = useState(false);

    // ==================== Effects ====================
    useEffect(() => {
        if (show && invoice) {
            fetchCreditInvoiceData();
        }
    }, [show, invoice]);

    // ==================== API Calls ====================
    const fetchCreditInvoiceData = async () => {
        try {
            const response = await apiCall.get(
                `/InvoiceEdit/${invoice?.invoiceNumber}?requestType=1`
            );
            const data = response.data;
            
            setIsRequestApproved(data.isRequestApproved);
            setIsRequestCreated(data.isEditInvoiceCreated);
            setIsCreditInvoiceAccept(data.isAnyResponseOnEditCreditInvoice);
            
            const userComment = data.invoiceEditItems[0]?.userComment;
            const referenceData = data.invoiceEditItems[0]?.requestedValue;
            
            if (userComment) setDescription(userComment);
            if (referenceData) setReference(referenceData);
        } catch (error) {
            console.error('Error fetching credit invoice data:', error);
        }
    };

    const handleRequestCreditInvoice = async () => {

        setReferenceError(false);
        setLoading(true);

        try {
            const requestBody = {
                invoiceId: invoice?.id,
                invoiceNumber: invoice?.invoiceNumber,
                id: 0,
                requestedBy: 0,
                requestStatus: 0,
                requestType: 1,
                isEditInvoiceCreated: false,
                isAnyResponseOnEditCreditInvoice: false,
                isRequestApproved: null,
                invoiceEditItems: [
                    {
                        userComment: description.trim(),
                        fieldName: "UserComment",
                        requestedValue: reference.trim(),
                        originValue: "",
                        approvedBy: null,
                        actualAmount: null
                    }
                ],
            };

            await apiCall.post('/CreditInvoice', requestBody);
            
            notify.success(
                `${t("THE_CREDIT_NOTE_REQUEST_HAS_BEEN_SENT_SUCCESSFULLY")} #${invoice?.invoiceNumber}`
            );
            
            onClose();
            onSuccess?.();
        } catch (error: any) {
            console.error('Error requesting credit invoice:', error);
            notify.error(
                error?.response?.data?.message ||
                t("AN_ERROR_OCCURRED_WHILE_REQUESTING_CREDIT_INVOICE")
            );
        } finally {
            setLoading(false);
        }
    };

    // ==================== Handlers ====================
    const handleClose = () => {
        if (!loading) {
            // Reset modal state when closing
            setReference('');
            setDescription('');
            setReferenceError(false);
            setIsRequestCreated(false);
            setIsCreditInvoiceAccept(false);
            setIsRequestApproved(false);
            
            onClose();
        }
    };

    const handleEditInvoice = () => {
        if (onEditInvoice) {
            onEditInvoice(invoice);
            handleClose();
        }
    };

    // ==================== Render Helpers ====================
    const renderStatusBadge = () => (
        <div className="status-badge-wrapper">
            <div className={`status-badge ${isRequestApproved ? 'approved' : 'declined'}`}>
                {t(isRequestApproved ? "APPROVED" : "DECLINED")}
            </div>
            <p className="status-message">
                {isRequestApproved 
                    ? t("YOUR_CREDIT_NOTE_REQUEST_HAS_BEEN_APPROVED")
                    : t("YOUR_CREDIT_NOTE_REQUEST_HAS_BEEN_DECLINED")}
            </p>
        </div>
    );

    const renderPendingStatus = () => (
        <div className="pending-status-wrapper">
            <p className="status-message">
                <strong>{t("ALREADY_REQUESTED")}</strong> - {t("YOUR_REQUEST_IS_PENDING_APPROVAL")}
            </p>
        </div>
    );

    const renderRequestForm = () => (
        <div className="request-form-wrapper">
            <p className="form-subtitle">
                {t("PLEASE_CONFIRM_IF_YOU_WANT_TO_REQUEST_CREDIT_INVOICE")}
            </p>
            
            {/* Invoice Info Box */}
            <div className="invoice-info-box">
                <span className="info-label">{t("Invoice")}:</span>
                <span className="info-value">#{invoice?.invoiceNumber}</span>
            </div>

            {/* Edit Invoice Button */}
            {onEditInvoice && (
                <div className="edit-invoice-button-wrapper">
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={handleEditInvoice}
                        disabled={loading}
                    >
                        {t("EDIT_INVOICE") || "Edit Invoice"}
                    </button>
                </div>
            )}


            {/* Description Field */}
            <div className="form-group">
                <label htmlFor="description" className="form-label">
                    {t("CREDIT_INVOICE_DESCRIPTION_LABEL")}
                </label>
                <textarea
                    id="description"
                    className="form-control"
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t("CREDIT_INVOICE_DESCRIPTION_PLACEHOLDER")}
                    maxLength={500}
                    disabled={loading}
                />
            </div>
        </div>
    );

    const renderActionButtons = () => (
        <div className="modal-footer mt-3">
            <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={handleClose}
                disabled={loading}
            >
                {t("Cancel")}
            </button>
            <button
                type="button"
                className="btn btn-primary"
                onClick={handleRequestCreditInvoice}
                disabled={!description.trim() || loading}
            >
                {loading ? (
                    <>
                        <span className="spinner"></span>
                        <span>{t('PROCESSING')}</span>
                    </>
                ) : (
                    t("YES")
                )}
            </button>
        </div>
    );

    const renderCloseButton = () => (
        <div className="modal-footer pt-3">
            <button 
                type="button" 
                className="btn btn-primary" 
                onClick={handleClose}
            >
                {t("Close")}
            </button>
        </div>
    );

    // ==================== Early Return ====================
    if (!show) return null;

    // ==================== Render ====================
    // The modal mounts inline in the parent table tree, which sits inside
    // `.manage-invoice-v2__panel`. That panel's entrance animation leaves a
    // residual `transform` on the element, and per the CSS spec any non-`none`
    // transform creates a containing block for `position: fixed` descendants.
    // Without a portal, our `position: fixed; top:0; left:0; right:0; bottom:0`
    // overlay would be measured against the panel — so the modal appears
    // centered inside the data table rather than the viewport. Portalling to
    // `document.body` escapes the transform and lets the overlay cover the
    // whole screen as intended.
    return ReactDOM.createPortal(
        <div
            className="request-credit-invoice-modal-overlay"
            onClick={handleClose}
            role="presentation"
        >
            <div 
                className="request-credit-invoice-modal-container" 
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="credit-invoice-modal-title"
            >
                {/* Header Section */}
                <div className="modal-header pb-3">
                    <h2 
                        className="modal-title"
                        id="credit-invoice-modal-title"
                    >
                        {t("REQUEST_CREDIT_INVOICE")}
                    </h2>
                    <button 
                        className="modal-close-btn" 
                        onClick={handleClose}
                        disabled={loading}
                        type="button"
                        aria-label="Close modal"
                    >
                        <svg 
                            width="24" 
                            height="24" 
                            viewBox="0 0 24 24" 
                            fill="none"
                            aria-hidden="true"
                        >
                            <path
                                d="M18 6L6 18M6 6L18 18"
                                stroke="#252525"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                </div>

                {/* Body Section */}
                <div className="modal-body">
                    {isRequestCreated && isCreditInvoiceAccept 
                        ? renderStatusBadge()
                        : isRequestCreated 
                            ? renderPendingStatus()
                            : renderRequestForm()
                    }
                </div>

                {/* Footer Section */}
                {isRequestCreated
                    ? renderCloseButton()
                    : renderActionButtons()
                }
            </div>
        </div>,
        document.body
    );
};

export default RequestCreditInvoiceModal;
